import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, User, Heart, LogOut, Search } from 'lucide-react';
import { Button } from './ui/button';
import { useState, useEffect, useCallback } from 'react';
import logoImg from '../assets/logo.png';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Header() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasNotification, setHasNotification] = useState(false);

  const navItems = [
    { to: '/feed', label: 'Explorar', icon: Search },
    { to: '/matches', label: 'Contactos', icon: Heart },
    { to: '/profile', label: 'Mi perfil', icon: User },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const checkNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const authHeaders = {
        withCredentials: true,
        headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) }
      };

      const response = await axios.get(`${API}/matches`, authHeaders);
      const data = response.data;
      const total = (data.matches?.length || 0) + (data.pending?.length || 0);
      const lastTotal = parseInt(localStorage.getItem('lastContactsTotal') || '0');
      const lastVisit = parseInt(localStorage.getItem('lastContactsVisit') || '0');
      localStorage.setItem('lastContactsTotal', total);
      if (total > lastTotal && Date.now() - lastVisit > 10000) {
        setHasNotification(true);
        return;
      }

      for (const match of (data.matches || [])) {
        try {
          const res = await axios.get(`${API}/messages/${match.user_id}`, authHeaders);
          const messages = res.data.messages || [];
          if (messages.length === 0) continue;
          const lastMsg = messages[messages.length - 1];
          if (lastMsg.sender_id === match.user_id) {
            const key = `lastReadMsg_${match.user_id}`;
            const lastRead = localStorage.getItem(key);
            if (lastRead !== lastMsg.created_at) {
              setHasNotification(true);
              return;
            }
          }
        } catch (e) {}
      }
    } catch (e) {}
  }, [user]);

  useEffect(() => {
    if (location.pathname === '/matches') {
      setHasNotification(false);
      localStorage.setItem('lastContactsVisit', Date.now());
      localStorage.setItem('lastContactsTotal', '0');
    }
  }, [location.pathname]);

  useEffect(() => {
    checkNotifications();
    const interval = setInterval(checkNotifications, 15000);
    return () => clearInterval(interval);
  }, [checkNotifications]);

  return (
    <header className="sticky top-0 z-50 bg-[hsl(40,50%,99%)]/90 backdrop-blur-xl border-b border-[hsl(35,15%,90%)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo → siempre va a /feed */}
          <Link to="/feed" className="flex items-center gap-2.5" data-testid="header-logo">
            <img src={logoImg} alt="Partykatu" className="h-9 w-auto" />
            <span className="font-heading font-semibold text-lg text-[hsl(144,18%,38%)] tracking-tight" translate="no">Partykatu</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link key={item.to} to={item.to}>
                <Button
                  variant="ghost"
                  className={`rounded-full ${isActive(item.to) ? 'bg-[hsl(144,18%,90%)] text-[hsl(144,18%,35%)]' : 'text-[hsl(20,10%,40%)] hover:text-[hsl(144,18%,35%)]'}`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <div className="relative">
                    <item.icon className="w-4 h-4 mr-2" />
                    {item.to === '/matches' && hasNotification && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                    )}
                  </div>
                  {item.label}
                </Button>
              </Link>
            ))}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="rounded-full text-[hsl(20,10%,40%)] hover:text-[hsl(0,72%,51%)] hover:bg-[hsl(0,72%,95%)] ml-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </nav>

          {/* Mobile Nav Icons */}
          <div className="md:hidden flex items-center gap-1">
            {navItems.map((item) => (
              <Link key={item.to} to={item.to}>
                <button
                  className={`p-2 rounded-full relative ${isActive(item.to) ? 'bg-[hsl(144,18%,90%)] text-[hsl(144,18%,35%)]' : 'text-[hsl(20,10%,40%)]'}`}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-6 h-6" />
                  {item.to === '/matches' && hasNotification && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </button>
              </Link>
            ))}
            <button
              className="p-2 text-[hsl(20,10%,40%)]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-[hsl(35,15%,90%)] space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl ${
                  isActive(item.to) ? 'bg-[hsl(144,18%,90%)] text-[hsl(144,18%,35%)]' : 'text-[hsl(20,10%,40%)]'
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
            <button
              onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-[hsl(0,72%,51%)] w-full"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Cerrar sesión
            </button>
          </nav>
        )}
      </div>
    </header>
  );
}
